// eslint-disable-next-line jira/restricted/react-component-props
import React, { Component, type ComponentType, type ComponentProps } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import { Picker } from '@atlassian/jira-common-components-picker/src/base/index.tsx';
import type { SelectOption } from '@atlassian/jira-common-components-picker/src/index.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { requestVersions } from '../../services/index.tsx';
import { transformVersion } from '../../services/transformers.tsx';
import messages from '../messages.tsx';
import type { MultiVersionProps } from '../types.tsx';

type PropsWithIntl = MultiVersionProps & {
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
export class MultiVersionPicker extends Component<PropsWithIntl> {
	static defaultProps = {
		isRequired: false,
		isDisabled: false,
		isClearableWithCrossIcon: false,
		baseUrl: '',
		noPortal: false,
		value: undefined,
		label: '',
		placeholder: undefined,
		ignore: [],
		versionStatuses: [],
		onSelected: noop,
	};

	onMultiSelected = (
		selectedOptions: ReadonlyArray<SelectOption>,
		analyticsEvent?: UIAnalyticsEvent,
	) =>
		this.props.onSelected(
			selectedOptions.map((o) => ({ id: o.value, name: o.label })),
			analyticsEvent,
		);

	getVersionIdsToIgnore(): string[] {
		const { value, ignore } = this.props;
		if (value) {
			return [...value.map((v) => v.id), ...ignore];
		}
		return ignore;
	}

	onFetchOptions = (filter: string) =>
		requestVersions(
			this.props.baseUrl,
			this.props.projectKey,
			filter,
			this.props.versionStatuses,
			this.getVersionIdsToIgnore(),
			this.props.excludeArchivedVersions,
		);

	getPlaceholder(): string {
		const {
			intl: { formatMessage },
		} = this.props;

		return this.props.placeholder === undefined
			? formatMessage(messages.placeholder)
			: this.props.placeholder;
	}

	getValue() {
		const { value } = this.props;
		if (value === null || value === undefined) {
			return value;
		}
		return value.map<SelectOption>(transformVersion);
	}

	render() {
		const { label, isRequired, isDisabled, intl, noPortal, isClearableWithCrossIcon } = this.props;

		return (
			<Picker
				isMulti
				value={this.getValue()}
				label={label}
				placeholder={this.getPlaceholder()}
				isRequired={isRequired}
				isDisabled={isDisabled}
				isClearableWithCrossIcon={isClearableWithCrossIcon}
				errorMessage={intl.formatMessage(messages.error)}
				onMultiSelected={this.onMultiSelected}
				options={this.onFetchOptions}
				noPortal={noPortal}
				intl={intl}
			/>
		);
	}
}

export default injectIntl(
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	withFireUiAnalytics({
		onSelected: 'versionPicker',
	})(MultiVersionPicker) as ComponentType<
		JSX.LibraryManagedAttributes<
			typeof MultiVersionPicker,
			ComponentProps<typeof MultiVersionPicker>
		>
	>,
);
